// features/tutorials/components/ModuleGrid.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { ModuleCard } from './ModuleCard';
import type { TutorialModule, TutorialLesson } from '../types';

interface ModuleGridProps {
    modules: TutorialModule[];
    onModuleClick: (module: TutorialModule) => void;
    onLessonClick: (lesson: TutorialLesson) => void;
    expandedModuleId: string | null;
    getModuleProgress: (moduleId: string) => number;
    isLessonCompleted: (lessonId: string) => boolean;
    isModuleCompleted: (moduleId: string) => boolean;
}

export const ModuleGrid: React.FC<ModuleGridProps> = ({
    modules,
    onModuleClick,
    onLessonClick,
    expandedModuleId,
    getModuleProgress,
    isLessonCompleted,
    isModuleCompleted,
}) => {
    return (
        <div className="mb-12">
            <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center justify-between mb-6"
            >
                <h2 className="text-3xl font-bold text-slate-900 dark:text-white">
                    Learning Modules
                </h2>
                <div className="text-sm text-slate-500 dark:text-slate-400">
                    {modules.filter((m: TutorialModule) => isModuleCompleted(m.id)).length}/{modules.length} completed
                </div>
            </motion.div>

            <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                initial="hidden"
                animate="visible"
                variants={{
                    visible: {
                        transition: {
                            staggerChildren: 0.1
                        }
                    }
                }}
            >
                {modules.map((module: TutorialModule) => (
                    <ModuleCard
                        key={module.id}
                        module={module}
                        progress={getModuleProgress(module.id)}
                        isExpanded={expandedModuleId === module.id}
                        isCompleted={isModuleCompleted(module.id)}
                        onModuleClick={() => onModuleClick(module)}
                        onLessonClick={onLessonClick}
                        isLessonCompleted={isLessonCompleted}
                    />
                ))}
            </motion.div>
        </div>
    );
};